import { useNavigate, useParams } from "react-router-dom";
import { vehicleTypeSchema } from "../../Schema/generalSchema";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery } from "react-query";
import { editVehicleType, getVehicleTypeDetails } from "../../Services/VehicleType";
import { Error, Success } from "../../Components/Modal";
import { useEffect, useState } from "react";
import BackButton from "../../Components/BackButton";
import { image_url } from "../../Utils/connection_strings";
import VehicleTypeForm from "./Components/VehicleTypeForm";
import "../CMSManagement/CMSManagement.css";
import "../../styles/PremiumCardPages.css";

export default function EditVehicleType() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);

    const formMethods = useForm({
        resolver: yupResolver(vehicleTypeSchema),
    });

    const { register, handleSubmit, formState: { errors }, control, reset, watch, setValue } = formMethods;

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const { data, isLoading: isFetching } = useQuery(
        ["getVehicleTypeDetails", id],
        () => getVehicleTypeDetails(id),
        { refetchOnWindowFocus: false }
    );

    useEffect(() => {
        if (data?.data?.data?.docs?.length > 0) {
            const vehicle = data?.data?.data?.docs?.[0];
            reset({
                name: vehicle?.name,
                cost: vehicle?.baseFare,
                weightLimit: vehicle?.weight ?? vehicle?.weightLimit,
                status: String(vehicle?.active) ?? "true",
                isHelperRequired: vehicle?.helperCount > 0,
                helperCount: vehicle?.helperCount || 0,
            });
            const imgPath = vehicle?.vehicleImage || vehicle?.image;
            if (imgPath && !image) {
                const clean_url = image_url?.endsWith("/") ? image_url.slice(0, -1) : image_url;
                const fullUrl = imgPath.startsWith("http")
                    ? imgPath
                    : `${clean_url}/${imgPath.startsWith("/") ? imgPath.slice(1) : imgPath}`;
                setPreview(fullUrl);
            }
        }
    }, [data, reset, image]);

    const { mutate, isLoading: loadingStatus } = useMutation(
        (formData) => editVehicleType(id, formData),
        {
            onSuccess: (res) => {
                Success("Vehicle Type", res.data?.message);
                navigate("/vehicle-type-management");
            },
            onError: (err) => {
                Error(err?.response?.data?.message);
            },
        }
    );

    const submit = (data) => {
        const formData = new FormData();
        formData.append("name", data?.name);
        formData.append("baseFare", data?.cost);
        formData.append("weight", data?.weightLimit);
        formData.append("active", data?.status);
        formData.append("helperCount", data?.isHelperRequired ? data?.helperCount : 0);
        formData.append("isHelperRequired", data?.isHelperRequired);
        formData.append("typeName", "vehicleImage");
        if (image) formData.append("vehicleImage", image);
        mutate(formData);
    };

    return (
        <div className="app-content content pform-page">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="edit-vehicle-type">
                        <div className="user-details-wrapper">
                            <div className="pform-page-header">
                                <div className="pform-page-header__row">
                                    <BackButton />
                                    <h1 className="pform-page-header__title">Edit Vehicle Type</h1>
                                </div>
                                <p className="pform-page-header__subtitle">
                                    Update vehicle pricing, capacity, and helper labor settings
                                </p>
                            </div>

                            <div className="pform-shell pform-shell--wide">
                                {isFetching ? (
                                    <div className="pform-loading">
                                        <div className="pform-loading__spinner" />
                                        <span>Loading vehicle type data...</span>
                                    </div>
                                ) : (
                                    <VehicleTypeForm
                                        register={register}
                                        handleSubmit={handleSubmit}
                                        errors={errors}
                                        control={control}
                                        onSubmit={submit}
                                        isLoading={loadingStatus}
                                        isEdit
                                        preview={preview}
                                        handleImageChange={handleImageChange}
                                        isHelperRequired={watch("isHelperRequired")}
                                        helperCount={watch("helperCount")}
                                        setValue={setValue}
                                        watch={watch}
                                    />
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
